/* Mobile Menu Accordion — Compat version (buttons or anchors)
   - Works with:
     • <button aria-controls="submenu-id"> ... </button>
     • <a class="has-submenu" aria-controls="submenu-id"> … </a>
     • <a class="has-submenu"> … </a><ul class="submenu">…</ul>
   - Intercepts ONLY on mobile, ONLY when an inline submenu element exists.
   - Keeps the main drawer open; expands downward in place; closes siblings.
*/
(() => {
  if (window.__menuAccordionCompatBound) return;
  window.__menuAccordionCompatBound = true;

  const MQ = '(max-width: 990px)';
  const isMobile = () => window.matchMedia(MQ).matches;

  const SELECTORS = {
    drawer:
      '.menu-drawer, [id*="menu-drawer"], .Drawer.MenuDrawer, .header-drawer',
    triggers:
      // strict buttons first, then anchors that might act as parents
      'button[aria-controls], a[aria-controls], a.has-submenu, .menu__item--parent > a, .menu__item--parent > button',
    siblingPanels: 'ul, .menu-drawer__submenu, .submenu, nav[role="navigation"]'
  };

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function findDrawer() {
    return document.querySelector(SELECTORS.drawer);
  }

  function preparePanel(panel) {
    if (panel.__prepared) return;
    panel.__prepared = true;
    panel.style.overflow = 'hidden';
    panel.style.transition = 'height .25s ease';
    // Respect current visibility
    const visible =
      !(panel.hasAttribute('hidden') || panel.getAttribute('aria-hidden') === 'true') &&
      getComputedStyle(panel).display !== 'none';
    panel.style.height = visible ? panel.scrollHeight + 'px' : '0px';
  }

  function expand(panel, trigger) {
    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', 'false');
    panel.style.display = 'block';
    panel.style.height = '0px';
    panel.offsetHeight; // reflow
    panel.style.height = panel.scrollHeight + 'px';
    trigger.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
  }

  function collapse(panel, trigger) {
    panel.style.height = panel.scrollHeight + 'px';
    panel.offsetHeight; // reflow
    panel.style.height = '0px';
    const finish = () => {
      panel.setAttribute('hidden', '');
      panel.setAttribute('aria-hidden', 'true');
      panel.style.display = '';
      panel.removeEventListener('transitionend', finish);
    };
    panel.addEventListener('transitionend', finish);
    trigger.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
  }

  function closeSiblings(currentTrigger, panel, drawer) {
    const li = currentTrigger.closest('li');
    const parentList = li && li.parentElement;
    if (!parentList) return;

    [...parentList.children].forEach((sib) => {
      if (sib === li) return;
      const sibTrigger =
        sib.querySelector(':scope > button[aria-controls], :scope > a[aria-controls], :scope > a.has-submenu, :scope > .menu__item--parent > a, :scope > .menu__item--parent > button');
      if (!sibTrigger) return;

      const sibPanel = resolvePanel(sibTrigger, drawer);
      if (sibPanel && sibPanel !== panel && !sibPanel.hasAttribute('hidden')) {
        collapse(sibPanel, sibTrigger);
      }
    });
  }

  function resolvePanel(trigger, drawer) {
    // Priority 1: aria-controls ID
    const ac = trigger.getAttribute('aria-controls');
    if (ac) {
      const byId = drawer.querySelector(`#${CSS.escape(ac)}`);
      if (byId) return byId;
    }
    // Priority 2: immediate sibling submenu
    const sib = trigger.nextElementSibling;
    if (sib && sib.matches(SELECTORS.siblingPanels)) return sib;

    // Priority 3: within the same LI, find first submenu
    const li = trigger.closest('li');
    if (li) {
      const nested = li.querySelector(SELECTORS.siblingPanels);
      if (nested) return nested;
    }
    return null;
  }

  function bind(drawer) {
    if (!drawer || drawer.__accordionCompatReady) return;
    drawer.__accordionCompatReady = true;

    drawer.addEventListener('click', (e) => {
      if (!isMobile()) return;

      const trigger = e.target.closest(SELECTORS.triggers);
      if (!trigger || !drawer.contains(trigger)) return;

      const panel = resolvePanel(trigger, drawer);
      if (!panel) return; // no inline submenu -> let navigation proceed

      // We have a real inline submenu: treat as accordion
      e.preventDefault();
      e.stopPropagation();

      preparePanel(panel);

      const willOpen = panel.style.height === '' || panel.style.height === '0px';
      closeSiblings(trigger, panel, drawer);

      if (willOpen) expand(panel, trigger);
      else collapse(panel, trigger);
    }, true); // capture phase to beat theme’s own handler
  }

  function attachWhenDrawerExists() {
    const init = () => {
      const drawer = findDrawer();
      if (drawer) bind(drawer);
    };

    // Observe DOM for the drawer mounting
    const mo = new MutationObserver(init);
    mo.observe(document.documentElement, { childList: true, subtree: true });

    // Try a few times on load
    onReady(() => {
      setTimeout(init, 50);
      setTimeout(init, 150);
      setTimeout(init, 400);
    });

    // Also re-bind on resize crossing breakpoint
    window.matchMedia(MQ).addEventListener?.('change', init);
    window.addEventListener('shopify:section:load', init);
  }

  attachWhenDrawerExists();
})();
